import { useMemo } from 'react';

import dayjs from 'shared/helpers/dayjs';
import { Event } from 'events/_types';
import { GuidanceQuestion } from 'priorities/_types';
import { InlinePriorityItem } from 'priorities/components/InlinePriorities/_index';

interface InlinePriorityGroups {
  routineQuestions: InlinePriorityItem[];
  eventUpdateQuestions: InlinePriorityItem[];
  carouselItems: InlinePriorityItem[];
}

export default function useInlinePriorityGroups(
  guidanceQuestions: GuidanceQuestion[] | undefined,
  selectedDate: dayjs.Dayjs,
  events: Event[]
): InlinePriorityGroups {
  const eventSummariesById = useMemo(() => {
    const map = new Map<string, string>();
    events.forEach((event) => {
      const text = event.summary || event.shift_notes?.[0]?.edited_text;
      if (text) map.set(event.id, text);
    });
    return map;
  }, [events]);

  const { routineQuestions, eventUpdateQuestions } = useMemo(() => {
    const routine: InlinePriorityItem[] = [];
    const eventUpdate: InlinePriorityItem[] = [];
    (guidanceQuestions ?? [])
      .filter((question) => dayjs(question.created).isSame(selectedDate, 'day'))
      .forEach((question) => {
        const item: InlinePriorityItem = {
          id: question.id,
          prompt: question.text,
          variant: question.event ? 'updateEvent' : 'routine',
          previousUpdate: question.event
            ? eventSummariesById.get(question.event)
            : undefined,
          eventId: question.event ?? undefined,
        };
        if (question.event) {
          eventUpdate.push(item);
        } else {
          routine.push(item);
        }
      });
    return { routineQuestions: routine, eventUpdateQuestions: eventUpdate };
  }, [guidanceQuestions, eventSummariesById, selectedDate]);

  const carouselItems = useMemo(
    () => [...routineQuestions, ...eventUpdateQuestions],
    [routineQuestions, eventUpdateQuestions]
  );

  return { routineQuestions, eventUpdateQuestions, carouselItems };
}
